/*
 * ////////////////////////////////////////////////////////////////////////////////
 * //
 * // This software system consists of computer software and documentation.
 * // It contains trade secrets and confidential information which are proprietary
 * // to Everi Games Inc.  Its use or disclosure in whole or in part without
 * // the express written permission of Everi Games Inc. is prohibited.
 * //
 * // This software system is also an unpublished work protected under the copyright
 * // laws of the United States of America.
 * //
 * // Copyright © 2018 Everi Games Inc.  All Rights Reserved
 * //
 * ////////////////////////////////////////////////////////////////////////////////
 */
import AudioManager from '../Audio/AudioManager';
import DtModObservable from '../Game/DtModObservable';
import SymbolData from '../Symbol/SymbolData';
import SymbolSet from '../Symbol/SymbolSet';
import InjectableComponent from '../Utility/Component/ComponentReference/InjectableComponent';
import ComponentBase from '../Utility/ComponentBase';
import CLog, { LogTypes } from '../Utility/ComponentLogger';
import ReelContents from './ReelContents';
import { GetReelEventName, ReelEvent } from './ReelEvent';
import ReelManager from './ReelManager';
import StateMachine from './StateMachine';
import { SpinStates } from './States/SpinStates';
import ReelSymbol from './Symbol';
import SymbolDimmer from './SymbolDimmer';

/** @hidden */
// tslint:disable-next-line:typedef
const property = cc._decorator.property;

/** Represents a reel on a slot machine, controlled by a [[ReelManager]]. */
@cc._decorator.ccclass
export default class Reel extends ComponentBase
{
    // #region Properties

    /** How many symbols are visible on this reel at any given time, includes half-stops. */
    @property({ type: cc.Integer, visible: true })
    private numVisibleSymbols: number = 3;

    /** A blank, base symbol to use for skinning the reel with a [[SymbolSet]]. */
    @property(cc.Prefab)
    private symbolPrefab: cc.Prefab = null;

    /** The mask used to keep the symbols from showing outside the reel. */
    @property(cc.Mask)
    private reelMask: cc.Mask = null;

    /** The height, in pixels, that the symbols should be scaled to. */
    @property({ type: cc.Integer, tooltip: "Pixels" })
    private symbolHeight: number = 175;
    public get SymbolHeight(): number { return this.symbolHeight; }

    /** The delay, in seconds, before the reel should start spinning. */
    @property ({ type: cc.Float, tooltip: "Seconds" })
    private startDelay: number = 0.0;

    @property({ visible: true })
    private _disablePatching: boolean = false;

    /** Whether or not this reel is in the process of spinning up. */
    @property({ visible: true })
    private _spinningUp: boolean = false;
    /** Mutator for setting whether or not the reel is in the process of spinning up. */
    public set SpinningUp(value: boolean) { this._spinningUp = value; }
    /** Accessor for whether or not the reel isin the process of spinning up. */
    public get SpinningUp(): boolean { return this._spinningUp; }

    /** The [[StateMacine]] used to control this reel. */
    @property(StateMachine)
    private stateMachine: StateMachine = null;

    /** The audio cue to play on reel spinning start. */
    @property()
    protected reelStartAudioTrigger: string = 'Reel.Start';
    /** Override audio trigger, reset each spin. */
    protected overrideReelStartAudioTrigger: string = null;
    /** Override audio trigger, reset each spin. */
    public set OverrideReelStartAudioTrigger(value: string) { this.overrideReelStartAudioTrigger = value; }

    /** The audio cue to play while the reel is spinning. */
    @property()
    protected reelSpinningAudioTrigger: string = 'Reel.Spinning';
    /** Override audio trigger, reset each spin. */
    protected overrideReelSpinningAudioTrigger: string = null;
    /** Override audio trigger, reset each spin. */
    public set OverrideReelSpinningAudioTrigger(value: string) { this.overrideReelSpinningAudioTrigger = value; }

    /** The audio cue to play when the reel stops spinning. */
    @property()
    protected reelStopAudioTrigger: string = 'Reel.Stop';
    /** Override audio trigger, reset each spin. */
    protected overrideReelStopAudioTrigger: string = null;
    /** Override audio trigger, reset each spin. */
    public set OverrideReelStopAudioTrigger(value: string) { this.overrideReelStopAudioTrigger = value; }

    @property()
    protected symbolZIndexTopDown: boolean = true;

    /** @hidden */
    private _audioManagerRef: InjectableComponent<AudioManager> = new InjectableComponent();
    /** @hidden */
    private _dtModRef: InjectableComponent<DtModObservable> = new InjectableComponent();

    /** The symbol string being used by this reel. */
    private _currentSymbolString: string = "";
    /** The current visible position of the reel strip, in pixels. */
    private _curVisiblePositionPx: number = 0;
    /** The amount of time that the reel has been spinning, in seconds. */
    private _elapsedSpinTime: number = 0;
    /** The symbol string representing a half-stop. */
    private _halfStopSymbol: string = null;
    /** Whether the reel has landed. */
    private _hasLanded: boolean = true;
    /** Whether the reel has landed. */
    public get HasLanded(): boolean { return this._hasLanded; }
    /** Whether the reel has landed and finished bouncing. */
    private _hasStopped: boolean = false;
    /** Whether the reel has landed and finished bouncing. */
    public get HasStopped(): boolean { return this._hasStopped; }
    /** Whether the reel is currently spinning. */
    private _isSpinning: boolean = false;
    /** How many symbols are queued up off screen and not visible. */
    private _offscreenSymbolCount: number = 2;
    /** Stop delay prior to any changes or modifications. */
    private _originalStopDelay: number;
    private _originalZIndex: number = 0;

    /** Whether or not this reel is about to quickstop. */
    private _pendingQuickStop: boolean = false;
    /** Where the reel is about to splice too to reach its stop position. */
    private _pendingSplicePosition: number = -1;
    /** The [[cc.Node]] which holds the various components which represent the reel. */
    private _reelContentsNode: cc.Node = null;
    /** Which reel is this in the machine. */
    private _reelIndex: number;
    /** Which reel is this in the machine. */
    public get ReelIndex(): number { return this._reelIndex; }
    /** The [[ReelManager]] that owns and controls this reel. */
    private _reelManager: ReelManager = null;
    /** The reel strip of symbol strings being used by this reel. */
    private _reelStrip: string[] = [];
    /** For odd numbers of visible symbols, the landing position is offset upward by 1/2 symbol height. */
    private _rootOffset: number = 0;
    /** For odd numbers of visible symbols, the landing position is offset upward by 1/2 symbol height. */
    public get RootOffset(): number { return this._rootOffset; }
    /** How fast should this reel spin, in symbols per second. */
    private _spinSpeed: number = 0;
    /** How fast should this reel spin, in pixels per second. */
    public set SpinSpeed(value: number) { this._spinSpeed = value; }
    /** How fast should this reel spin, in pixels per second. */
    public get SpinSpeed(): number { return this._spinSpeed; }
    /** The state in the reel [[StateMachine]] where a spin can be initiated from. */
    private _spinState: SpinStates = SpinStates.Stopped;
    public get SpinState(): SpinStates { return this._spinState; }
    /** If the reel is currently locked. */
    private _isLocked: boolean = false;
    public get IsLocked(): boolean { return this._isLocked; }
    public set IsLocked(value: boolean)
    {
        if (this._isLocked === value) { return; }

        this._isLocked = value;
        this.node.dispatchEvent(Reel.getReelEvent(value ? ReelEvent.OnLocked : ReelEvent.OffLocked));
    }
    /** If the reel is currently enabled for play. */
    private _isEnabled: boolean = true;
    public get IsEnabled(): boolean { return this._isEnabled; }
    public set IsEnabled(value: boolean)
    {
        if (this._isEnabled !== value)
        {
            this._isEnabled = value;

            if (this._isEnabled) { this.node.dispatchEvent(Reel.getReelEvent(ReelEvent.OnEnabled)); }
            else { this.node.dispatchEvent(Reel.getReelEvent(ReelEvent.OnDisabled)); }
        }
    }

    /** The delay before the reel stops on its own. */
    private _stopDelay: number = 0.0;
    /** The delay before the reel stops on its own. */
    public get StopDelay(): number { return this._stopDelay; }
    /** The delay before the reel stops on its own. */
    public set StopDelay(value: number) { this._stopDelay = value; }
    /** The position on the reel strip where the reel should stop. */
    private _stopPosition: number = 0;
    /** True if [[Reel]] is synced to another reel. */
    private _isSynced: boolean = false;
    /** Whether or not the reel is approaching a stop. */
    private _stopQueued: boolean = false;
    /** The symbol set used for this reel. */
    private _symbolSet: SymbolSet = null;

    /** Whether the reel has moved at least one symbol. */
    private _movedSymbols: number = 0;
    public get MovedSymbols(): number { return this._movedSymbols; }

    /**
     * This is to ensure that we don't short-curcuit the reel delay
     * on a short reel strip by stopping at the first occurance of the symbol.
     */
    private _symbolsUntilStop: number = 0;
    /** The collection of visible symbols displayed by the reel. */
    private _visibleSymbols: cc.Node[] = [];
    /** The collection of visible symbols displayed by the reel. */
    public get VisibleSymbols(): cc.Node[] { return this._visibleSymbols; }
    /** The collection of visible symbols displayed by the reel. */
    public set VisibleSymbols(value: cc.Node[]) { this._visibleSymbols = value; }

    private readonly _maxFrameMovement: number = this.symbolHeight;
    private _reelDebugNode: cc.Node = null;
    private _debug: boolean = false;

    // #endregion

    // #region Computed Properties

    /**
     * Attempts to trigger the supplied audio cue.
     * @param trigger The audio cue to try and trigger.
     */
    protected tryTriggerAudio = (trigger: string): void =>
    {
        if (!trigger) { return; }
        this._audioManagerRef.WithValue(audioManager => audioManager.TriggerSound(null, trigger));
    }

    /**
     * Attempts to stop the trigger of the supplied audio cue.
     * @param trigger The audio cue to try and stop.
     */
    protected tryStopAudio = (trigger: string): void =>
    {
        if (!trigger) { return; }
        this._audioManagerRef.WithValue(audioManager => audioManager.StopTrigger(null, trigger));
    }

    /** Is this reel currently in the stopped state. */
    public get IsStopped(): boolean { return this._spinState === SpinStates.Stopped; }

    /** The reel strip's currently visible position, in pixels. */
    public get Position(): number { return this._curVisiblePositionPx; }

    /** Sets the reel strip's currently visible position, in pixels. */
    public set Position(pos: number)
    {
        if (isNaN(pos))
        {
            CLog.warn("Setting reel pos to NaN", LogTypes.Reel, this);
        }
        this._curVisiblePositionPx = pos;
    }

    /** The index for the current symbol that the reel should stop on. */
    public get StopSymbolIndex(): number
    {
        for (let symbolNodeIndex: number = 0; symbolNodeIndex < this._visibleSymbols.length; symbolNodeIndex++)
        {
            let symbolNode: cc.Node = this._visibleSymbols[symbolNodeIndex];
            let symbol: ReelSymbol = symbolNode.getComponent(ReelSymbol);
            if (symbol.ReelPosition === this._stopPosition)
            {
                // Check the surrounding visible symbols to make sure this is a valid stop.
                let range: number = Math.floor(this.numVisibleSymbols / 2);
                let isValid: boolean = true;
                for (let i: number = symbolNodeIndex - range; i <= symbolNodeIndex + range; i++)
                {
                    if (i >= 0 && i < this._visibleSymbols.length - 1)
                    {
                        let diff: number = this._visibleSymbols[i].getComponent(ReelSymbol).ReelPosition
                            - this._visibleSymbols[i + 1].getComponent(ReelSymbol).ReelPosition;
                        if (diff === 1 || diff === 1 - this._reelStrip.length)
                        {
                            continue;
                        }

                        isValid = false;
                        break;
                    }
                }

                if (isValid) { return symbolNodeIndex; }
            }
        }
        return -1;
    }

    /** Does this reel have half-stops. */
    private get hasHalfStops(): boolean
    {
        if (this._halfStopSymbol && this._halfStopSymbol !== "")
        {
            return this._reelStrip.indexOf(this._halfStopSymbol) >= 0;
        }

        return false;
    }

    /** The maxium symbol offset going down from the center row. */
    public get MaxOffset(): number
    {
        let maxOffset: number = Math.floor(this._visibleSymbols.length / 2);
        return maxOffset;
    }

    /** The minimum symbol offset going up from the center row. */
    public get MinOffset(): number
    {
        let minOffset: number = -this.MaxOffset;
        if (this._visibleSymbols.length % 2 === 0)
        {
            minOffset++;
        }
        return minOffset;
    }

    /** The Y-axis value, in pixels, of the bottom edge of the reel. */
    private get reelBottom(): number
    {
        if (this.reelMask == null)
        {
            return 0;
        }
        return this.reelMask.node.y - this.reelMask.node.height / 2;
    }

    /** The Y-axis value, in pixels, of the top edge of the reel. */
    private get reelTop(): number
    {
        if (this.reelMask == null)
        {
            return 0;
        }
        return this.reelMask.node.y + this.reelMask.node.height / 2;
    }

    // #endregion

    // #region Initialization

    /** @hidden */
    protected setContexts(): void
    {
        super.setContexts();
        this._audioManagerRef.SetContext(AudioManager);
        this._dtModRef.SetContext(DtModObservable);
    }

    /** @hidden */
    public Initialize(reelIndex: number, stopDelay: number, symbolSet: SymbolSet, halfStopSymbol: string): void
    {
        this._reelIndex = reelIndex;
        this._stopDelay = this._originalStopDelay = stopDelay;
        this._symbolSet = symbolSet;
        this._halfStopSymbol = halfStopSymbol;

        this._reelDebugNode = this.node.getChildByName("ReelDebugTxt");
        this._debug = CC_DEBUG && this._reelDebugNode !== null;

        this._originalZIndex = this.node.getLocalZOrder();

        this.initSymbolNodes();
        // Cause refresh on symbol positions.
        this._hasLanded = false;
    }

    /** @hidden */
    protected start(): void
    {
        super.start();

        this._reelManager = this.getFirstParent(ReelManager);
        this._reelContentsNode = this.getComponentInChildren(ReelContents).node;

        // For odd numbers of visible symbols, the landing position is offset upward by 1/2 symbol height
        this._rootOffset = this.numVisibleSymbols % 2 === 0 ? this.symbolHeight / 2 : 0;
    }

    /**
     * Helper that initializes the initial set of visible symbols to populate the reel.
     * @hidden
     */
    private initSymbolNodes(): void
    {
        const totalSymbols: number = this.numVisibleSymbols + this._offscreenSymbolCount;

        for (let n: number = 0; n < totalSymbols; n++)
        {
            this.addSymbolNode(n);
        }
    }

    /**
     * Helper that adds a new symbol node to the visible symbols set.
     * @hidden
     */
    private addSymbolNode(index: number): cc.Node
    {
        if (this._symbolSet == null) { return; }

        const symbolNode: cc.Node = cc.instantiate(this.symbolPrefab);
        symbolNode.parent = this._reelContentsNode;
        symbolNode.position = new cc.Vec2(0, this.reelBottom + (index * this.symbolHeight));

        const symbol: ReelSymbol = symbolNode.getComponent(ReelSymbol);

        // Preload animations..
        this._symbolSet.CopyAllAnimationsForSymbol(symbol);

        symbol.SymbolHeight = this.symbolHeight;
        symbol.Reel = this;

        this.addSymbolPositionDebug(symbolNode);
        this._visibleSymbols.push(symbolNode);

        const dimmer: SymbolDimmer = symbolNode.getComponent(SymbolDimmer);
        if (dimmer) { dimmer.ReelNode = this.node; }

        return symbolNode;
    }

    /**
     * For debug use only.
     * @DEBUG
     * @hidden
     */
    private addSymbolPositionDebug(node: cc.Node): void
    {
        if (!this._debug) { return; }
        let debugTextNode: cc.Node = node.getChildByName("DebugText");
        if (!debugTextNode) { return; }

        let debugDraw: cc.RichText = debugTextNode.getComponent(cc.RichText);
        if (debugDraw)
        {
            let symbol: ReelSymbol = node.getComponent(ReelSymbol);
            debugDraw.string = `<color=#0fffff>${symbol.ReelPosition} - ${this._visibleSymbols.indexOf(node)}`
                                + ` = ${Math.floor(this._curVisiblePositionPx)}</color>`;
        }
    }

    // #endregion

    // #region Update

    /** @hidden */
    public update(dt: number): void
    {
        if (this._hasLanded && this._spinState === SpinStates.Stopped) { return; }

        this._dtModRef.WithValue(dtMod => dt = dtMod.Multiply(dt));

        this.updateSpritePositions(dt);
        this.cycleSprites();
        this.drawDebug();
        this._elapsedSpinTime += dt;
    }

    /**
     * Checks either the top and/or bottom symbol node for cycling based on the reels current spinSpeed.
     * @hidden
     */
    private cycleSprites(): void
    {
        if (!this._reelStrip || !this._reelStrip.length) { return; }
        if (this.isForwardDirection) { this.cycleSymbolNode(true); }
        if (this.isBackwardDirection) { this.cycleSymbolNode(false); }
    }

    /** Is the reel spinning forwards. */
    private get isForwardDirection(): boolean
    {
        return this._spinSpeed >= 0;
    }

    /** Is the reel spinning backwards. */
    private get isBackwardDirection(): boolean
    {
        return this._spinSpeed <= 0;
    }

    /**
     * Checks either the top or bottom symbol node to see if they've gone off screen and need cycling.
     * @param isTop Whether to check the top or bottom symbol node for cycling.
     */
    private cycleSymbolNode(isTop: boolean): void
    {
        let symbolIndex: number = isTop ? this._visibleSymbols.length - 1 : 0;
        if (symbolIndex < 0) { return; }

        let symbolNode: cc.Node = this._visibleSymbols[symbolIndex];
        if (symbolNode == null) { return; }

        let direction: number = isTop ? -1 : 1;
        let symbol: ReelSymbol = symbolNode.getComponent(ReelSymbol);
        let symbolEdge: number = symbolNode.y + ((isTop ? -1 : 1) * (this.symbolHeight / 2));

        let isSymbolOnScreen: boolean = isTop ? symbolEdge < this.reelTop : symbolEdge > this.reelBottom;
        while (isSymbolOnScreen)
        {
            // (append || prepend) a new symbol
            isTop
                ? this._visibleSymbols.push(this._visibleSymbols.shift())
                : this._visibleSymbols.unshift(this._visibleSymbols.pop());

            // Position our newly cycled symbol node.
            let newSymbol: cc.Node = this._visibleSymbols[symbolIndex];
            let neighbor: cc.Node = this._visibleSymbols[symbolIndex + direction];
            newSymbol.y = neighbor.y + (this.symbolHeight * -direction);

            // Get the next reel strip position, either advancing normally or jumping to a splice point
            let nextReelPosition: number = -1;
            if (this._pendingSplicePosition >= 0 && (this._spinningUp !== isTop))
            {
                nextReelPosition = this.getReelPositionInRange(this._pendingSplicePosition);
                this._pendingSplicePosition = -1;
            }
            else
            {
                nextReelPosition = this.getNextReelPosition(symbol.ReelPosition, isTop);
            }

            // Prevent doubling up on half stops or reel symbols in mechanical games
            this._currentSymbolString = this._reelStrip[symbol.ReelPosition];
            let nextSymbolString: string = this._reelStrip[nextReelPosition];
            if (this.hasHalfStops &&
               ((this._currentSymbolString === this._halfStopSymbol && nextSymbolString === this._halfStopSymbol) ||
                (this._currentSymbolString !== this._halfStopSymbol && nextSymbolString !== this._halfStopSymbol)))
            {
                nextReelPosition = this.getNextReelPosition(nextReelPosition, isTop);
            }

            // Are we moving in the direction of spin
            if (isTop !== this._spinningUp)
            {
                this._symbolsUntilStop--;
            }

            // Assign the new symbol's properties to the cycled symbol node.
            newSymbol = this.assignVisibleSymbolTexture(newSymbol, nextReelPosition);

            if (newSymbol != null)
            {
                // attach symbol and update properties.
                newSymbol.parent = this._reelContentsNode;
                this._visibleSymbols[symbolIndex] = newSymbol;
                this._curVisiblePositionPx += this.symbolHeight * direction;
            }

            // Update symbol node reference.
            symbolNode = this._visibleSymbols[symbolIndex];

            // Update symbol reference.
            symbol = symbolNode.getComponent(ReelSymbol);

            // Update edge.
            symbolEdge = symbolNode.y + (direction * (this.symbolHeight / 2));

            // Update direction.
            isSymbolOnScreen = isTop
                ? symbolEdge < this.reelTop
                : symbolEdge > this.reelBottom;
        }

        symbol.OffSet = null;
        symbol.IsReelFace = false;

        this._movedSymbols++;
        this.reIndexSymbols();
    }

    private reIndexSymbols(): void
    {
        this._visibleSymbols.forEach((symbolNode, index) =>
        {
            symbolNode.zIndex = this.symbolZIndexTopDown ? index : this._visibleSymbols.length - index;
        });
    }

    /**
     * Returns the next reel position based on the current reel position and the direction of spin.
     * @param reelPos The current reel position.
     * @param forward Is the reel spinning forward.
     * @returns The next reel position.
     */
    private getNextReelPosition = (reelPos: number, forward: boolean): number =>
    {
        reelPos += (forward ? -1 : 1);

        return this.getReelPositionInRange(reelPos);
    }

    /**
     * Checks if a reel position is in the acceptable range and updates it if not.
     * @param reelPos The reel position to check.
     * @returns The acceptable reel position.
     */
    private getReelPositionInRange(reelPos: number): number
    {
        if (reelPos < 0 || reelPos >= this._reelStrip.length)
        {
            reelPos += this._reelStrip.length * (reelPos < 0 ? 1 : -1);
        }

        return reelPos;
    }

    /**
     * Updates the spinning action of the reel.
     * @hidden
     */
    private updateSpinning(): void
    {
        if (!this._isSpinning)
        {
            this.tryTriggerAudio(this.overrideReelSpinningAudioTrigger
                ? this.overrideReelSpinningAudioTrigger
                : this.reelSpinningAudioTrigger);
        }

        this._isSpinning = true;

        if (this._stopQueued)
        {
            this.SetSpinState(SpinStates.StopInitiated);
            this._stopQueued = false;
        }
    }

    /**
     * Updates the reel's sprite position based on the current reel state.
     * @param dt Delta time.
     * @hidden
     */
    private updateSpritePositions(dt: number): void
    {
        switch (this._spinState)
        {
            case SpinStates.Spinning: this.updateSpinning(); break;
            case SpinStates.StopInitiated: this.updateStopInitiated(); break;
            case SpinStates.Stopping: this.updateStopping(); break;
            case SpinStates.Bounce: this.updateLanded(); break;
            case SpinStates.Stopped: this.updateStopped(); break;
        }

        let pxPerSec: number = this._spinSpeed * this.symbolHeight;
        let pxDelta: number = pxPerSec * dt;

        if (pxDelta > this._maxFrameMovement) { pxDelta = this._maxFrameMovement; }

        this._curVisiblePositionPx += pxDelta;

        for (let symbolNode of this._visibleSymbols)
        {
            let symbol: ReelSymbol = symbolNode.getComponent(ReelSymbol);

            if (this._spinState !== SpinStates.Stopped)
            {
                symbol.UpdateReelPosition(this._curVisiblePositionPx);
            }
            else
            {
                symbol.UpdateStopped();
            }
        }
    }

    /**
     * Register a callback to be performed each time the reel stops.
     * @param fnOnStopped The function to call when the reel stops.
     */
    public OnStopped(fnOnStopped: (args?: any) => any): void
    {
        this.node.on(GetReelEventName(ReelEvent.OnStopped), fnOnStopped);
    }

    /**
     * Register a callback to be performed the next time the reel stops.
     * @param fnOnStopped The function to call when the reel stops.
     */
    public OnStoppedOnce(fnOnStopped: (args?: any) => any): void
    {
        this.node.once(GetReelEventName(ReelEvent.OnStopped), fnOnStopped);
    }

    /**
     * Update the landing action of the reel.
     * @hidden
     */
    private updateLanded(): void
    {
        if (this._hasLanded) { return; }

        this._hasLanded = true;

        this.updateSymbolOffsets();

        if (this._reelManager.Reels.every((reel) => reel.HasLanded))
        {
            this.scheduleOnce(() => this.tryStopAudio(this.overrideReelSpinningAudioTrigger
                ? this.overrideReelSpinningAudioTrigger
                : this.reelSpinningAudioTrigger), .01);
        }

        this.scheduleOnce(() =>
        {
            this.tryTriggerAudio(this.overrideReelStopAudioTrigger
                ? this.overrideReelStopAudioTrigger
                : this.reelStopAudioTrigger);
        }, .01);

        this.node.dispatchEvent(Reel.getReelEvent(ReelEvent.OnLanded));
    }

    /** Updats symbol offsets based on position in currrent visible symbols. */
    private updateSymbolOffsets(): void
    {
        let symbolCount: number = this.MaxOffset;
        for (let symbolNode of this._visibleSymbols)
        {
            let symbol: ReelSymbol = symbolNode.getComponent(ReelSymbol);

            symbol.OffSet = symbolCount--;
            // Is under max / over min.
            symbol.IsReelFace = symbol.ReelPosition < this.MaxOffset + this._stopPosition;
            symbol.IsReelFace = symbol.IsReelFace && symbol.ReelPosition > this.MinOffset + this._stopPosition;
        }
    }

    /**
     * Update the stopping action of the reel.
     * @hidden
     */
    private updateStopped(): void
    {
        if (this._hasStopped) { return; }

        this._hasStopped = true;
        this._isSpinning = false;
    }

    /**
     * Update the stop initiation of the reel.
     * @hidden
     */
    private updateStopInitiated(): void
    {
        if (this._pendingQuickStop && !this._disablePatching)
        {
            this.minPatchReel();
        }
        else
        {
            this.patchReel();
        }

        this._pendingQuickStop = false;
        this.SetSpinState(SpinStates.Stopping);
    }

    /**
     * Update the stopping action of the reel.
     * @hidden
     */
    private updateStopping(): void
    {
        if (this.StopSymbolIndex < 0) { return; }

        const visibleOffsetPx: number = (this.StopSymbolIndex) * this.symbolHeight - this._rootOffset;

        // If fps drops while a symbol is half off screen, then were to animate
        // at the max of one symbol's height per frame we could miss the landing
        // by a fraction of the symbol. So we coushin with one symbol height.
        let reached: boolean = visibleOffsetPx < this._curVisiblePositionPx + this._maxFrameMovement;

        if (this.SpinningUp) { reached = !reached; }

        if (reached && this._symbolsUntilStop <= 1)
        {
            this.Position = visibleOffsetPx;
            this.SetSpinState(SpinStates.Bounce);
        }
    }

    /**
     * Update the reel strip's visible position.
     * @hidden
     */
    private updateReelPosition(): void
    {
        const visibleOffsetPx: number = (this.StopSymbolIndex) * this.symbolHeight - this._rootOffset;
        this.Position = visibleOffsetPx;
    }

    // #endregion

    // #region Public Functions

    /**
     * Finds the symbols string at the specified index in the reel strip.
     * @param reelStripIndex The symbol's reel strip index.
     * @returns The symbol string.
     */
    public GetSymbol(reelStripIndex: number): string
    {
        return this._reelStrip[reelStripIndex];
    }

    /**
     * Finds the [[ReelSymbol]] at the specified offset.
     * @param offset The offset from the center row (0) on the reel.
     * @returns The [[ReelSymbol]] at the specified offset.
     */
    public GetSymbolAtOffset(offset: number): ReelSymbol
    {
        if (this.StopSymbolIndex === -1) { return null; }

        let symbolNode: cc.Node = this._visibleSymbols[this.StopSymbolIndex - offset];
        return symbolNode ? symbolNode.getComponent(ReelSymbol) : null;
    }

    /**
     * Returns the index of the supplied symbol node.
     * @param node The node of the symbol being searched for.
     * @returns The index of the supplied symbol node.
     */
    public GetVisibleSymbolIndex(node: cc.Node): number
    {
        return this._visibleSymbols.indexOf(node);
    }

    /**
     * Processes the reel strip returned from the RGS.
     * @param strReelStrip The reel strip supplied by the RGS.
     */
    public OnReceiveReelStrip(aReelStrip: string[]): void
    {
        this._reelStrip = aReelStrip;
    }

    /**
     * Patches the reel strip and stops the reel as soon as possible.
     */
    public QuickStop(): void
    {
        switch (this._spinState)
        {
            case SpinStates.Stopping:
                if (this._symbolsUntilStop > 0 && !this._disablePatching)
                {
                    this.minPatchReel();
                }
                break;
            case SpinStates.Started:
            case SpinStates.Cockback:
            case SpinStates.RampingUp:
            case SpinStates.Spinning:
                this._pendingQuickStop = true;
                break;
            default:
                return;
        }
    }

    public InstantQuickStop(delayMS: number = 0): void
    {
        if (this.IsLocked || !this.IsEnabled) { return; }

        this.scheduleOnce(() =>
            {
                this.minPatchReel(false);
                this._pendingQuickStop = false;
            }, delayMS / 1000);
    }

    /**
     * Sets the current state of the reel [[StateMachine]].
     * @param state The [[State]] to be set.
     */
    public SetSpinState(state: SpinStates): void
    {
        if (this._spinState === state) { return; }

        // CLog.log(`Reel [ ${this._reelIndex} ]: ${state}`, LogTypes.Reel, this);
        this._spinState = state;

        this.emitCurrentReelState();

        this.stateMachine.SetState(state.toString());
    }

    /**
     * Attached to on reel.
     * @param reelEvent [[ReelEvent]] to execute on.
     * @param fnOnReelEvent Function to execute.
     */
    public OnReelEvent(reelEvent: ReelEvent, fnOnReelEvent: (event: cc.Event.EventCustom) => void): void
    {
        this.node.on(GetReelEventName(reelEvent), fnOnReelEvent);
    }

    /**
     * Attached to on reel for once execution.
     * @param reelEvent [[ReelEvent]] to execute on.
     * @param fnOnReelEvent Function to execute.
     */
    public OnReelEventOnce(reelEvent: ReelEvent, fnOnReelEvent: (event: cc.Event.EventCustom) => void): void
    {
        this.node.once(GetReelEventName(reelEvent), fnOnReelEvent);
    }

    /** Emit current [[SpinState]] matching [[ReelEvent]] through node. */
    private emitCurrentReelState = (): void =>
    {
        switch (this._spinState)
        {
            case SpinStates.Spinning:
                this.emitReelState(ReelEvent.OnStarted);
                break;
            case SpinStates.Stopping:
                this.emitReelState(ReelEvent.OnStopping);
                break;
            case SpinStates.Stopped:
                this.emitReelState(ReelEvent.OnStopped);
                break;
            default:
                break;
        }
    }

    private emitReelState(event: ReelEvent): void
    {
        this.node.dispatchEvent(Reel.getReelEvent(event));
    }

    private static getReelEvent = (reelEvent: ReelEvent): cc.Event.EventCustom =>
    {
        return new cc.Event.EventCustom(GetReelEventName(reelEvent), true);
    }

    /**
     * Triggers the reel to start spinning.
     */
    public Spin(): void
    {
        if (this.IsLocked || !this.IsEnabled) { return; }

        this.resetSpin();
        this.SetSpinState(SpinStates.Started);
        this.scheduleOnce(this.delayedSpin, this.startDelay);

        this.tryTriggerAudio(this.overrideReelStartAudioTrigger
            ? this.overrideReelStartAudioTrigger
            : this.reelStartAudioTrigger);
    }

    /**
     * Stops this reel at the specificed stop position on the reel strip.
     * @param stopPosition The position on the reel strip to stop at.
     */
    public Stop(stopPosition: number): void
    {
        if (this.IsLocked || !this.IsEnabled) { return; }

        this._stopQueued = true;
        this._stopPosition = stopPosition;
        this._symbolsUntilStop = 0;
    }

    /**
     * Short circuits normal reel stop and quickstop functionality.
     * @param stopPosition The position on the reel strip to stop at.
     */
    public StopImmediate(stopPosition: number): void
    {
        if (this.IsLocked || !this.IsEnabled) { return; }

        this._stopPosition = stopPosition;
        this.setSymbolsToCurrentStop();
        this.updateReelPosition();
        this.SetSpinState(SpinStates.Stopped);
    }


    public ForceUpdateSymbols(): void
    {
        if (this._symbolSet)
        {
            for (let symbolNode of this._visibleSymbols)
            {
                let symbol: ReelSymbol = symbolNode.getComponent(ReelSymbol);
                const strSymbolID: string = this.GetSymbol(symbol.ReelPosition);

                if(!strSymbolID)
                {
                    continue;
                }

                symbol.SymbolData = this._symbolSet.GetSymbolDataById(strSymbolID);
                symbol.SetActive(strSymbolID);
            
            }
        }
    }

    // #endregion

    // #region Private Helpers

    /**
     * Helper function for assigning a new symbol to an existing symbol node.
     * @param node The visible symbol node to be updated.
     * @param reelStripIndex The index on the reel strip to update the symbol node to.
     * @returns The updated symbol node.
     */
    private assignVisibleSymbolTexture(node: cc.Node, reelStripIndex: number): cc.Node
    {
        const strSymbolID: string = this.GetSymbol(reelStripIndex);
        const symbol: ReelSymbol = node.getComponent(ReelSymbol);

        if (!this._symbolSet || !strSymbolID)
        {
            CLog.warn('Invalid symbol configuration.', LogTypes.Reel, this);
            return null;
        }

        symbol.SymbolData = this._symbolSet.GetSymbolDataById(strSymbolID);
        symbol.ReelPosition = reelStripIndex;
        symbol.SymbolHeight = this.symbolHeight;
        symbol.Reel = this;

        symbol.SetActive(strSymbolID);
        return node;
    }

    /** @hidden */
    private delayedSpin = (): void =>
    {
        if (this._spinState === SpinStates.Started)
        {
            this.SetSpinState(SpinStates.Cockback);
        }
    }

    /**
     * FOR DEBUG ONLY
     * @hidden
     */
    private drawDebug(): void
    {
        if (!this._debug) { return; }

        this._reelDebugNode.getComponent(cc.RichText).string
            = `<color=#0000ff>${Math.floor(this._curVisiblePositionPx)}</color>`;
    }

    /**
     * Helper for getting the symbol node at the specified reel offset.
     * @param offset The reel offset to get the symbol node for.
     * @returns The symbol node.
     */
    private getSymbolNodeAtOffset(offset: number): cc.Node
    {
        return this._visibleSymbols[this.getSymbolNodeIndexAtOffset(offset)];
    }

    /**
     * Helper for getting the symbol node's index
     * @param offset The reel offset to get the symbol node index for.
     * @returns The symbol node index.
     */
    private getSymbolNodeIndexAtOffset(offset: number): number
    {
        return offset - this.MinOffset;
    }

    /**
     * Helper function that patches the reel to the stop position as quickly as possible.
     * @hidden
     */
    private minPatchReel(checkStopSymbol: boolean = true, extraBufferSymbols: number = 0): void
    {
        this._symbolsUntilStop = 0;
        let stopIndex: number = this.StopSymbolIndex;

        // IF the stop symbol isn't already on the reel,
        // OR if the reel is spinning backward and the symbol has already passed its stop,
        // OR if the reel is spinning forward and the symbol has already passed its stop,
        // splice the symbol stop position in.
        if (!checkStopSymbol || stopIndex < 0 ||
            (this.SpinningUp && stopIndex > Math.floor(this._visibleSymbols.length / 2)) ||
            (!this.SpinningUp && stopIndex < Math.floor(this._visibleSymbols.length / 2)))
        {
            // Add extra padding on quick stop before patching in target stop to avoid bad reel faces.
            this.splice((this._stopPosition + Math.ceil(this._visibleSymbols.length / 2)) + extraBufferSymbols
                % this._reelStrip.length);
        }
    }

    /**
     * Helper that patches the reel as it spins through the reel strip.
     * @hidden
     */
    private patchReel(): void
    {
        const symbolDelay: number = this._spinSpeed * (this._stopDelay - this._elapsedSpinTime);

        // Given the current speed of the reel and how much time is left until stopping,
        // how many symbols will we still pass
        this._symbolsUntilStop = Math.abs(Math.floor(symbolDelay));

        if (this._disablePatching) { return; }

        if (this._spinSpeed > 0 && symbolDelay < 0)
        {
            this.minPatchReel();
            return;
        }

        // What symbol index should show up next, in order to reach the target stop
        const direction: number = this.SpinningUp ? -1 : 1;

        let nextSymbolIndex: number = Math.floor(this._stopPosition + (this._symbolsUntilStop * direction));
        while (nextSymbolIndex < 0)
        {
            nextSymbolIndex = this._reelStrip.length + nextSymbolIndex;
        }
        nextSymbolIndex %= this._reelStrip.length;
        this.splice(nextSymbolIndex);
    }

    /** @hidden */
    private resetSpin(): void
    {
        this._spinSpeed = 0;
        this._movedSymbols = 0;
        this._elapsedSpinTime = 0;

        this._hasLanded = false;
        this._hasStopped = false;
        this._stopQueued = false;
        this._isSpinning = false;

        this.overrideReelStopAudioTrigger = null;

        this.resetSyncedReels();
    }

    /** @hidden */
    private setSymbolsToCurrentStop(): void
    {
        for (let offset: number = this.MinOffset; offset <= this.MaxOffset; offset++)
        {
            const stopPos: number = this.wrap(this._stopPosition - offset, 0, this._reelStrip.length);
            let symbolNode: cc.Node = this.getSymbolNodeAtOffset(offset);

            symbolNode = this.assignVisibleSymbolTexture(symbolNode, stopPos);
            symbolNode.parent = this._reelContentsNode;

            this._visibleSymbols[this.getSymbolNodeIndexAtOffset(offset)] = symbolNode;
        }

        this.reIndexSymbols();
    }

    /**
     * Sets the pending splice position for the next reel patch.
     * @param position Position to patch to.
     * @hidden
     */
    private splice(position: number): void
    {
        this._pendingSplicePosition = position;
    }

    /**
     * Helper function that wraps a given value to stay within a given range.
     * @param value The value to wrap.
     * @param min The minimum value.
     * @param max The maximum value.
     * @hidden
     */
    private wrap(value: number, min: number, max: number): number
    {
        let range: number = max - min;
        while (value >= max) { value -= range; }
        while (value < min) { value += range; }
        return value;
    }

    /** @ hidden */
    private resetSyncedReels(): void
    {
        this._isSynced = false;
        this.ResetStopDelay();
        this.node.setLocalZOrder(this._originalZIndex);
    }

    /**
     * Syncs separate reels together.
     * @param reel The reel to sync with.
     */
    public SyncReel(reel: Reel): void
    {
        this._isSynced = true;

        this.ResetElapsedTime();

        this._stopPosition = reel._stopPosition;
        this._symbolsUntilStop = reel._symbolsUntilStop;

        this.setSymbolsToCurrentStop();

        this.minPatchReel(false);
    }

    /**
     * Manually clear the elapsed spin time.
     */
    public ResetElapsedTime = (): void =>
    {
        this._elapsedSpinTime = 0.0;
    }

    /**
     * Manually reset the stop delay back to its original value.
     */
    public ResetStopDelay = (): void =>
    {
        this.ResetElapsedTime();

        this._stopDelay = this._originalStopDelay;
    }

    /**
     * Increases the current stop delay by the specified amount.
     * @param amount The amount, in seconds, to increase the stop delay by.
     */

    public AddStopDelay(amount: number = 0, override: boolean = false): void
    {
        if (this._isSynced) { return; }

        this._reelManager.CanQuickStop = false;
        this._pendingQuickStop = false;

        if (!override) { this._stopDelay += amount; }

        if (this._hasLanded || this._hasStopped)
        {
            this.resetSpinning();
            if (override)
            {
                this._stopDelay = amount;
            }
        }
        else
        {
            this.patchReel();
        }
    }

    /** Resets a reel to full spin. */
    private resetSpinning(): void
    {
        this.resetSpin();

        this.node.emit(GetReelEventName(ReelEvent.OnResetSpinning));
        this.SetSpinState(SpinStates.Stopping);

        this.Stop(this._stopPosition);
    }

    // #endregion
}
